import { useEffect, useState } from 'react';
import { collection, doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import { useMock } from './mockContext';

const PING_PERIOD_MS = 30_000;
const PING_BUFFER_MS = 5_000;

/* useFamiliesData(familyIDs)

   Subscribes to live data for a list of families and returns
     { [familyID]: { familyName, autoMode, hasEverSignedIn, onlinePingMs,
                     screen, wifiRssiDbm, iglooLinks, isOnline } }

   Fields and how they're derived:

     familyName         familyInfo/{fid}.name
     autoMode           familyInfo/{fid}.autoMode (default false if missing,
                        but in practice the /setup batch sets this for
                        non-primary families to true)
     trainingComplete   familyInfo/{fid}.trainingComplete === true. Missing
                        or false both mean training is queued (the device
                        hasn't confirmed training is done yet). Only a
                        literal `true` unlocks the "Repeat training"
                        action in HearthDetail.
     hasEverSignedIn    familyInfo/{fid}.firstSignin exists. Canonical "this
                        Hearth has physically signed in at least once" signal —
                        written by /user/verify on first successful device
                        signin. Do NOT use deviceManagement doc existence: that
                        gets created pre-emptively by /setup (for targetAPK) so
                        it's true even for devices that have never connected.
     onlinePingMs       deviceManagement/{fid}/status/everyFamilyAndAccountVisible
                        .onlinePing (ts → ms)
     screen             deviceManagement/{fid}/status/accountVisible.location
                        ('home' | 'sleep' | 'training' | iglooID | iglooID-Position).
                        Matches OLD IndividualDashboard.deviceStatus.location.
                        Kept as `screen` on the hook output for consumer
                        stability — the underlying Firestore field is
                        `location` but the semantic is "what the TV is
                        currently showing" which reads as `screen` in the
                        UI layer.
     wifiRssiDbm        deviceManagement/{fid}/status/accountVisible.rssi
                        — same subdoc as location. OLD IndividualDashboard
                        reads `rssi` from here and feeds `WifiSignalBars`.
     iglooLinks         familyInfo/{fid}/igloos sub-collection
                        ([{iglooID, name, position}])
     isOnline           derived: onlinePingMs is within the last ping window
                        (and we tick every 10s so a stale ping flips offline
                        without needing a Firestore update)

   Designed to be cheap to subscribe to: each family attaches 4 listeners.
   On unmount or familyIDs change, all are torn down. */
export function useFamiliesData(familyIDs) {
  const mock = useMock();
  const [data, setData] = useState({});

  // Stable string key so the effect only re-runs when the set actually changes.
  const fidsKey = familyIDs ? familyIDs.slice().sort().join('|') : '';

  useEffect(() => {
    if (mock?.families) return undefined;
    if (!familyIDs || familyIDs.length === 0) {
      setData({});
      return undefined;
    }

    const update = (fid, patch) => {
      setData((prev) => ({ ...prev, [fid]: { ...(prev[fid] || {}), ...patch } }));
    };

    const unsubs = [];
    for (const fid of familyIDs) {
      // familyInfo top-level (name, autoMode, firstSignin)
      unsubs.push(
        onSnapshot(
          doc(db, 'familyInfo', fid),
          (snap) => {
            if (snap.exists()) {
              const d = snap.data();
              update(fid, {
                familyName: d.name || '',
                // autoMode defaults to false when missing — that matches the
                // /setup behaviour for primary families and is the safe default
                // anywhere else.
                autoMode: d.autoMode === true,
                // `trainingComplete === true` only. Missing or false both
                // mean training is queued and the action should read as
                // pending in HearthDetail.
                trainingComplete: d.trainingComplete === true,
                hasEverSignedIn: d.firstSignin != null,
              });
            } else {
              update(fid, { hasEverSignedIn: false });
            }
          },
          (err) => console.warn('familyInfo listener error', fid, err.code),
        ),
      );

      // onlinePing lives at deviceManagement/{fid}/status/everyFamilyAndAccountVisible
      // — NOT on the top-level deviceManagement/{fid} doc. Matches OLD
      // IndividualDashboard lines 618–636. An earlier version of this hook
      // read the top-level doc, which exists (it's written pre-emptively
      // by /user/setup for targetAPK) but never carries the live ping, so
      // isOnline was permanently false for every Hearth.
      unsubs.push(
        onSnapshot(
          doc(db, 'deviceManagement', fid, 'status', 'everyFamilyAndAccountVisible'),
          (snap) => {
            if (snap.exists()) {
              const d = snap.data();
              const ping = d.onlinePing;
              const pingMs = ping?.toMillis
                ? ping.toMillis()
                : ping?.toDate
                  ? ping.toDate().getTime()
                  : ping?.seconds
                    ? ping.seconds * 1000
                    : null;
              update(fid, { onlinePingMs: pingMs });
            } else {
              update(fid, { onlinePingMs: null });
            }
          },
          () => update(fid, { onlinePingMs: null }),
        ),
      );

      // Location (what the TV is showing) + WiFi signal live at
      // deviceManagement/{fid}/status/accountVisible, same as OLD
      // IndividualDashboard.js:638–658. The earlier log/system path this
      // hook used is stale — the TVApp writes the canonical data to the
      // accountVisible subdoc, which is also what the admin webapp reads.
      unsubs.push(
        onSnapshot(
          doc(db, 'deviceManagement', fid, 'status', 'accountVisible'),
          (snap) => {
            if (snap.exists()) {
              const d = snap.data();
              update(fid, {
                // `location` → `screen` in the hook output; semantics
                // unchanged so HearthCard / HearthDetail don't need to
                // care about the rename.
                screen: d.location || null,
                wifiRssiDbm: typeof d.rssi === 'number' ? d.rssi : null,
              });
            } else {
              update(fid, { screen: null, wifiRssiDbm: null });
            }
          },
          () => update(fid, { screen: null, wifiRssiDbm: null }),
        ),
      );

      // igloos sub-collection (per-family Higlo memberships with name + position)
      unsubs.push(
        onSnapshot(
          collection(db, 'familyInfo', fid, 'igloos'),
          (snap) => {
            const list = snap.docs.map((d) => ({ iglooID: d.id, ...d.data() }));
            update(fid, { iglooLinks: list });
          },
          () => update(fid, { iglooLinks: [] }),
        ),
      );
    }

    // Re-evaluate online status periodically so a Hearth flips to "offline"
    // without needing a Firestore push.
    const ticker = setInterval(() => setData((prev) => ({ ...prev })), 10_000);

    return () => {
      unsubs.forEach((u) => u());
      clearInterval(ticker);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fidsKey, mock]);

  if (mock?.families) return mock.families;

  // Annotate with derived isOnline (cheap, recomputed on each render).
  const annotated = {};
  for (const fid of familyIDs || []) {
    const f = data[fid] || {};
    const isOnline =
      !!f.onlinePingMs && Date.now() - f.onlinePingMs < PING_PERIOD_MS + PING_BUFFER_MS;
    annotated[fid] = { ...f, isOnline };
  }
  return annotated;
}
